import { useCallback, useMemo } from "react";
import {
  hasCompletePreferenceValues,
  normalizeCompletePreferences,
} from "../../services/completePreferences";
import { normalizeSimplifiedBankroll } from "../../services/dataModel";
import {
  applyOnboardingPlatformsToBankroll,
  buildCompletePreferencesFromOnboarding,
} from "./onboardingPreferences";

const hasPositiveMoneyValue = (value) => {
  const parsed = Number(String(value || "").replace(",", "."));
  return Number.isFinite(parsed) && parsed > 0;
};

const hasSimplifiedData = (bankroll) => {
  const normalized = normalizeSimplifiedBankroll(bankroll);
  return Boolean(
    normalized.accounts.length ||
      normalized.history.length ||
      hasPositiveMoneyValue(normalized.base) ||
      Object.keys(normalized.platforms).length
  );
};

export default function useOnboardingFlow({
  dataLoaded,
  userId,
  tournaments,
  registros,
  completePreferences,
  setCompletePreferences,
  simplifiedBankroll,
  setSimplifiedBankroll,
  setDisplayCurrency,
}) {
  const normalizedPreferences = useMemo(
    () => normalizeCompletePreferences(completePreferences),
    [completePreferences]
  );

  const shouldShowOnboarding = Boolean(
    userId &&
      dataLoaded &&
      !normalizedPreferences.onboardingCompletedAt &&
      !hasCompletePreferenceValues(normalizedPreferences) &&
      !tournaments.length &&
      !registros.length &&
      !hasSimplifiedData(simplifiedBankroll)
  );

  const completeOnboarding = useCallback(
    ({ buyInMin, buyInMax, platforms, displayCurrency }) => {
      const completedAt = new Date().toISOString();

      setCompletePreferences(
        buildCompletePreferencesFromOnboarding({
          currentPreferences: normalizedPreferences,
          buyInMin,
          buyInMax,
          platforms,
          completedAt,
        })
      );
      setSimplifiedBankroll((current) =>
        applyOnboardingPlatformsToBankroll(current, platforms)
      );
      setDisplayCurrency(displayCurrency);
    },
    [
      normalizedPreferences,
      setCompletePreferences,
      setDisplayCurrency,
      setSimplifiedBankroll,
    ]
  );

  return {
    shouldShowOnboarding,
    completeOnboarding,
  };
}
